"use client";

import { useEffect, useState } from "react";
import { Mountain } from "lucide-react";

const STAGES = {
  workspace: { percent: 0, label: "Preparing the map workspace" },
  data: { percent: 25, label: "Loading runs, lifts and routes" },
  renderer: { percent: 50, label: "Preparing the map display" },
  tiles: { percent: 75, label: "Drawing the map and route network" },
} as const;

/** Completed loading stages, not guessed download bytes or a timer percentage. */
export function MountainMapLoading({ stage, className = "h-[600px] md:h-[680px]" }: {
  stage: keyof typeof STAGES;
  className?: string;
}) {
  const { percent, label } = STAGES[stage];
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 15000);
    return () => clearTimeout(timer);
  }, []);

  return <div className={`flex w-full items-center justify-center rounded-[18px] bg-warm-bg px-6 text-navy ${className}`}>
    <div className="w-full max-w-xs text-center">
      <Mountain size={32} aria-hidden className="mx-auto mb-4 text-teal motion-safe:animate-pulse" />
      <p className="font-heading text-lg font-bold">Loading your mountain map</p>
      <p role="status" className="mt-2 text-sm text-text-muted">{label}…</p>
      <div role="progressbar" aria-label="Mountain map loading" aria-valuemin={0} aria-valuemax={100}
        aria-valuenow={percent} aria-valuetext={`${percent}%, ${label}. Progress by loading stage.`}
        className="mt-5 h-2 overflow-hidden rounded-full bg-navy/10">
        <div className="h-full rounded-full bg-teal motion-safe:transition-[width]" style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 text-sm font-semibold tabular-nums">{percent}%</p>
      <p className="mt-1 text-xs text-text-muted">Progress by loading stage</p>
      {slow && <p role="status" className="mt-4 text-sm text-text-muted">Still loading. Detailed maps can take a little longer on a slow connection.</p>}
    </div>
  </div>;
}
